import dayjs, { Dayjs } from 'dayjs';
import {
  Alert,
  Button,
  Card,
  Divider,
  Empty,
  Flex,
  Form,
  FormProps,
  Input,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Select,
  Typography
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DesktopOutlined,
  FolderOpenOutlined,
  InfoCircleOutlined,
  ProfileOutlined,
  ReadOutlined,
  SafetyCertificateOutlined,
  StarOutlined
} from '@ant-design/icons';
import { DateRangeField } from 'components/forms/fields/DateRangeField';
import { FieldsetTitle } from 'components/professor_area/FieldsetTitle';
import { FormItemCol } from 'components/professor_area/FormItemCol';
import {
  canEditClassroomCriteria,
  isClassroomPeriodClosed
} from 'utils/classroom-form';
import { getYearRangeData } from 'utils/create-year-selector-data';
import { toSaoPauloTimeZone } from 'services/date';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelect } from '@refinedev/antd';
import type { ComponentType, ReactNode } from 'react';
import type { IClassroom } from 'interfaces/classrooms';
import type { IGraduateDiscipline } from 'interfaces/graduate_disciplines';

export interface IClassroomPost extends IClassroom {
  dateRange: Dayjs[];
  dateRangeContent: Dayjs[];
  dateRangeDiscussion: Dayjs[];
  frequency_required?: boolean;
  progress_required?: boolean;
}

interface ClassroomFormProps {
  discipline?: {
    id: string;
    name: string;
  };
  formProps: FormProps<IClassroomPost>;
  edit?: boolean;
}

const yearArrayData = getYearRangeData(1999, 10);

const convertToTimeZoneRange = (startDate?: string, endDate?: string) => {
  return [
    startDate ? toSaoPauloTimeZone(startDate) : null,
    endDate ? toSaoPauloTimeZone(endDate) : null
  ];
};

const clearCriterionWhenOptional = (
  form: FormProps<IClassroomPost>['form'],
  field: 'minimum_attendance' | 'minimum_progress_percentage',
  required: boolean
) => {
  if (!required) {
    form?.setFieldValue(field, null);
  }
};

export const EditClassroomForm = ({
  formProps,
  discipline,
  edit
}: ClassroomFormProps) => {
  const [minimunAttendance, setMinimunAttendance] = useState(false);
  const [progressRequired, setProgressRequired] = useState(false);

  const isClassroomClosed = useMemo(() => {
    if (!edit) {
      return false;
    }

    return isClassroomPeriodClosed(formProps.initialValues?.ends_at);
  }, [edit, formProps.initialValues?.ends_at]);

  const canEditCriteria = useMemo(
    () => canEditClassroomCriteria(!!edit, formProps.initialValues?.ends_at),
    [edit, formProps.initialValues?.ends_at]
  );

  const onChangeFrequency = useCallback(
    (event: RadioChangeEvent) => {
      const required = event.target.value;
      setMinimunAttendance(required);
      clearCriterionWhenOptional(
        formProps.form,
        'minimum_attendance',
        required
      );
    },
    [formProps.form]
  );

  const onChangeProgress = useCallback(
    (event: RadioChangeEvent) => {
      const required = event.target.value;
      setProgressRequired(required);
      clearCriterionWhenOptional(
        formProps.form,
        'minimum_progress_percentage',
        required
      );
    },
    [formProps.form]
  );

  const onCopyDurationDate = useCallback(
    (formField: string) => {
      const durationDate = formProps.form?.getFieldValue('dateRange');

      formProps.form?.setFieldValue(formField, durationDate);
    },
    [formProps.form]
  );

  const initialValues = useMemo(() => {
    const initialValues = { ...formProps.initialValues };

    initialValues.dateRange = convertToTimeZoneRange(
      initialValues?.starts_at,
      initialValues?.ends_at
    );

    initialValues.dateRangeContent = convertToTimeZoneRange(
      initialValues?.content_access_starts_at,
      initialValues?.content_access_ends_at
    );

    initialValues.dateRangeDiscussion = convertToTimeZoneRange(
      initialValues?.discussion_starts_at,
      initialValues?.discussion_ends_at
    );

    return {
      ...initialValues,
      discipline_id: discipline?.id || null,
      discipline_name: discipline?.name || '',
      academic_year: initialValues.academic_year || dayjs().year(),
      frequency_required: !!initialValues.minimum_attendance || false,
      progress_required: !!initialValues.minimum_progress_percentage || false
    } as Partial<IClassroomPost>;
  }, [formProps, discipline]);

  useEffect(() => {
    if (initialValues.minimum_attendance) {
      setMinimunAttendance(true);
    }
  }, [initialValues.minimum_attendance]);

  useEffect(() => {
    if (initialValues.minimum_progress_percentage) {
      setProgressRequired(true);
    }
  }, [initialValues.minimum_progress_percentage]);

  return (
    <Form {...formProps} layout="vertical" initialValues={initialValues}>
      <fieldset>
        <FieldsetTitle title="Informações Gerais" icon={ProfileOutlined} />

        <div className="pl-6">
          {!discipline?.id ? (
            <FormWithoutDiscipline />
          ) : (
            <>
              <Form.Item hidden noStyle name="discipline_id">
                <Input />
              </Form.Item>

              <FormItemCol name="discipline_name" label="Disciplina">
                <Input
                  disabled={!!discipline}
                  placeholder="Escreva o nome da disciplina"
                />
              </FormItemCol>
            </>
          )}

          <FormItemCol
            name="code"
            label="Código da turma"
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <Input placeholder="Ex.: F12908" disabled={edit} />
          </FormItemCol>

          {edit && (
            <FormItemCol name="number" label="ID da turma">
              <Input disabled />
            </FormItemCol>
          )}

          <FormItemCol
            name="description"
            label="Descrição (opcional)"
            itemsCenter={false}
          >
            <Input.TextArea
              rows={3}
              placeholder="Escreva uma breve descrição da turma"
            />
          </FormItemCol>

          <FormItemCol
            name="seats_total"
            label="Número de vagas"
            rules={[{ required: true, message: 'Campo obrigatório' }]}
          >
            <InputNumber className="max-w-[80px]" />
          </FormItemCol>
        </div>
      </fieldset>

      <Divider />

      <fieldset>
        <FieldsetTitle title="Período acadêmico" icon={ClockCircleOutlined} />

        <Flex gap={16} className="pl-6">
          <Form.Item
            label="Ano"
            layout="vertical"
            name="academic_year"
            className="mb-0 lg:w-[160px]"
          >
            <Select style={{ minWidth: 100 }}>
              {yearArrayData.map(year => (
                <Select.Option key={year.value} value={year.value}>
                  {year.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Período"
            name="academic_period"
            layout="vertical"
            className="mb-0"
            rules={[
              {
                required: true,
                message: 'Campo obrigatório'
              }
            ]}
          >
            <InputNumber className="lg:w-[160px]" />
          </Form.Item>
        </Flex>
      </fieldset>

      <Divider />

      <fieldset>
        <FieldsetTitle
          title="Inscrição automática"
          icon={CheckCircleOutlined}
          hint="Habilitar a inscrição dos alunos na turma automaticamente após a
        compra"
        />

        <div className="pl-6">
          <Form.Item noStyle name="auto_enroll" initialValue={true}>
            <Radio.Group
              options={[
                { label: 'Ativada', value: true },
                {
                  label: 'Desativada',
                  value: false
                }
              ]}
              optionType="button"
              buttonStyle="outline"
              size="middle"
            />
          </Form.Item>
        </div>
      </fieldset>

      <Divider />

      <fieldset>
        <FieldsetTitle
          title="Critérios de aprovação"
          icon={SafetyCertificateOutlined}
          hint="Defina quais critérios serão usados para aprovar o aluno nesta turma."
        />

        <Flex vertical gap={12} className="pl-6">
          <OngoingEnrollmentsAlert visible={!!edit} />

          <CriterionRow
            icon={StarOutlined}
            title="Nota mínima"
            description={
              canEditCriteria
                ? 'Nota mínima exigida para aprovação na turma. Informe um valor entre 0 e 100.'
                : 'A nota mínima não pode ser alterada após o encerramento da turma.'
            }
            config={
              <Form.Item
                name="passing_score"
                label="Nota mínima"
                className="mb-0"
                rules={
                  canEditCriteria
                    ? [{ required: true, message: 'Campo obrigatório' }]
                    : undefined
                }
              >
                <InputNumber
                  min={0}
                  max={100}
                  placeholder="0-100"
                  disabled={!canEditCriteria}
                  className="max-w-[80px]"
                />
              </Form.Item>
            }
          />

          <CriterionRow
            icon={DesktopOutlined}
            title="Frequência nas aulas ao vivo"
            description={
              canEditCriteria
                ? 'Use este critério para exigir presença mínima nas aulas ao vivo.'
                : 'A frequência mínima não pode ser alterada após o encerramento da turma.'
            }
            config={
              <Form.Item noStyle name="frequency_required">
                <Radio.Group
                  optionType="button"
                  buttonStyle="outline"
                  size="middle"
                  disabled={!canEditCriteria}
                  onChange={onChangeFrequency}
                >
                  <Radio.Button value={false}>Não obrigatória</Radio.Button>
                  <Radio.Button value={true}>
                    {minimunAttendance && <CheckOutlined className="mr-1" />}
                    Obrigatória
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            }
            extra={
              minimunAttendance ? (
                <Form.Item
                  name="minimum_attendance"
                  label="Frequência mínima"
                  className="mb-0"
                  rules={
                    canEditCriteria
                      ? [{ required: true, message: 'Campo obrigatório' }]
                      : undefined
                  }
                >
                  <InputNumber
                    type="number"
                    addonAfter="%"
                    min={0}
                    max={100}
                    disabled={!canEditCriteria}
                    placeholder="0-100"
                    className="max-w-[100px]"
                  />
                </Form.Item>
              ) : undefined
            }
          />

          <CriterionRow
            icon={ReadOutlined}
            title="Progresso no conteúdo"
            description={
              canEditCriteria
                ? 'Use este critério para exigir avanço mínimo no conteúdo assíncrono da turma.'
                : 'O progresso mínimo não pode ser alterado após o encerramento da turma.'
            }
            config={
              <Form.Item noStyle name="progress_required">
                <Radio.Group
                  optionType="button"
                  buttonStyle="outline"
                  size="middle"
                  disabled={!canEditCriteria}
                  onChange={onChangeProgress}
                >
                  <Radio.Button value={false}>Não obrigatória</Radio.Button>
                  <Radio.Button value={true}>
                    {progressRequired && <CheckOutlined className="mr-1" />}
                    Obrigatória
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            }
            extra={
              progressRequired ? (
                <Form.Item
                  name="minimum_progress_percentage"
                  label="Progresso mínimo"
                  className="mb-0"
                  rules={
                    canEditCriteria
                      ? [{ required: true, message: 'Campo obrigatório' }]
                      : undefined
                  }
                >
                  <InputNumber
                    type="number"
                    addonAfter="%"
                    min={0}
                    max={100}
                    disabled={!canEditCriteria}
                    placeholder="0-100"
                    className="max-w-[100px]"
                  />
                </Form.Item>
              ) : undefined
            }
          />
        </Flex>
      </fieldset>

      <Divider />

      <fieldset>
        <FieldsetTitle title=" Duração de acessos" icon={CalendarOutlined} />

        <Flex vertical gap={12} className="pl-6">
          {isClassroomClosed && (
            <Alert
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              message="Ao reabrir a turma, alunos reprovados que ainda não concluíram o curso terão a nota e a situação recalculadas."
              className="border-primary-100 bg-primary-50 text-neutral-700 [&_.ant-alert-icon]:text-primary-500"
            />
          )}

          <CriterionRow
            icon={CalendarOutlined}
            title="Vigência"
            description="Período em que o curso estará ativo"
            config={
              <DurationDateConfig
                name="dateRange"
                startsAtField="starts_at"
                endsAtField="ends_at"
                required
              />
            }
          />

          <CriterionRow
            icon={CommentOutlined}
            title="Período de tira-dúvidas"
            description="Até quando os alunos podem enviar dúvidas"
            config={
              <DurationDateConfig
                name="dateRangeDiscussion"
                startsAtField="discussion_starts_at"
                endsAtField="discussion_ends_at"
              />
            }
            extra={
              <Button
                variant="outlined"
                color="primary"
                size="small"
                className="!h-6 !px-2 !text-xs"
                onClick={() => onCopyDurationDate('dateRangeDiscussion')}
              >
                Copiar data da vigência
              </Button>
            }
          />

          <CriterionRow
            icon={FolderOpenOutlined}
            title="Acesso ao conteúdo"
            description="Até quando os alunos podem acessar o conteúdo do curso"
            config={
              <DurationDateConfig
                name="dateRangeContent"
                startsAtField="content_access_starts_at"
                endsAtField="content_access_ends_at"
              />
            }
            extra={
              <Button
                variant="outlined"
                color="primary"
                size="small"
                className="!h-6 !px-2 !text-xs"
                onClick={() => onCopyDurationDate('dateRangeContent')}
              >
                Copiar data da vigência
              </Button>
            }
          />
        </Flex>
      </fieldset>
    </Form>
  );
};

const DATE_RANGE_TIME_PROPS = {
  showTime: {
    defaultOpenValue: [dayjs('00:00', 'HH:mm'), dayjs('23:59', 'HH:mm')]
  }
};

const OngoingEnrollmentsAlert = ({ visible }: { visible: boolean }) => {
  if (!visible) {
    return null;
  }

  return (
    <Alert
      type="info"
      showIcon
      icon={<InfoCircleOutlined />}
      message="Alunos que já concluíram a turma não serão afetados. As alterações nos critérios valem apenas para matrículas em andamento."
      className="border-primary-100 bg-primary-50 text-neutral-700 [&_.ant-alert-icon]:text-primary-500"
    />
  );
};

const DurationDateConfig = ({
  name,
  startsAtField,
  endsAtField,
  required
}: {
  name: string;
  startsAtField: string;
  endsAtField: string;
  required?: boolean;
}) => (
  <Flex vertical className="min-w-0 w-full">
    <DateRangeField
      name={name}
      className="mb-0"
      startsAtField={startsAtField}
      endsAtField={endsAtField}
      dateRangeProps={DATE_RANGE_TIME_PROPS}
      rules={
        required
          ? [
              {
                required: true,
                message: 'Campo obrigatório'
              }
            ]
          : undefined
      }
    />
    <Typography.Text className="text-neutral-500 text-xs mt-1">
      Horário de Brasilia (GMT-3)
    </Typography.Text>
  </Flex>
);

const CRITERION_GRID =
  'grid grid-cols-1 md:grid-cols-[minmax(0,4fr)_minmax(0,3.5fr)_minmax(168px,2fr)] gap-4 md:gap-6 items-center';

const CriterionRow = ({
  icon,
  title,
  description,
  config,
  extra
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  description: string;
  config: ReactNode;
  extra?: ReactNode;
}) => (
  <Card className="bg-neutral-50" size="small">
    <div className={CRITERION_GRID}>
      <Flex gap={12} align="flex-start">
        <CriterionIcon icon={icon} />
        <Flex vertical>
          <span className="font-medium">{title}</span>
          <Typography.Text className="text-neutral-500">
            {description}
          </Typography.Text>
        </Flex>
      </Flex>

      <div className="min-w-0">{config}</div>

      <div
        className={
          extra ? 'min-w-0 border-neutral-200 md:border-l md:pl-6' : 'min-w-0'
        }
      >
        {extra}
      </div>
    </div>
  </Card>
);

const CriterionIcon = ({
  icon: IconComponent
}: {
  icon: ComponentType<{ className?: string }>;
}) => (
  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50">
    <IconComponent className="text-lg text-primary-500" />
  </div>
);

const FormWithoutDiscipline = () => {
  const {
    selectProps: disciplineSelectProps,
    queryResult: disciplineQueryResult
  } = useSelect<IGraduateDiscipline>({
    resource: 'graduate_disciplines',
    optionLabel: 'name',
    optionValue: 'id',

    onSearch: value => [
      {
        field: 'filter[q]',
        operator: 'eq',
        value
      }
    ],

    pagination: {
      mode: 'server',
      pageSize: 100
    }
  });

  const disciplines = useMemo(() => {
    return disciplineQueryResult?.data?.data.map(discipline => {
      const { name, id, external_reference } = discipline;

      const codExternal = external_reference ? `${external_reference} - ` : '';

      const label = `${codExternal} ${name}`;

      return {
        label,
        value: id
      };
    });
  }, [disciplineQueryResult]);

  return (
    <FormItemCol
      label="Disciplina"
      name="discipline_id"
      rules={[
        {
          required: true
        }
      ]}
    >
      <Select
        {...disciplineSelectProps}
        options={disciplines}
        style={{ minWidth: 200 }}
        notFoundContent={
          <Empty description="Nenhuma disciplina encontrada. Tente refazer sua busca com outros termos" />
        }
      />
    </FormItemCol>
  );
};
